'use strict';

import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {observer} from 'mobx-react';
import classnames from "classnames";

import Spinner from "react-bootstrap/Spinner";

import DocumentTitle from './DocumentTitle';
import UnknownState from "./UnknownState";
import Login from "./Login";
import Maintenance from "./Maintenance";
import Error from "./Error";
import Unauthorized from "./Unauthorized";
import FeedbackMessages from "./FeedbackMessages";
import Cookies from './Cookies';
import {States} from './AppConstants';
import {AppContext} from "./AppContext";
import Announcer from "./Announcer";
import AccessibleHashlink from "./AccessibleHashlink";
import AppFooterDefault from "./AppFooterDefault";
import AppHeaderDefault from "./AppHeaderDefault";
import PageTracking from "./PageTracking";
import BannerModal from "./BannerModal";
import {isEmpty} from "./Utils";

class App extends Component {

    // Set the app context to get access to AppStore in the session.
    static contextType = AppContext;

    constructor(props) {
        super(props);
        this.props = props;
    }

    handleBeforeUnload = async () => {
        await this.context.appStore.getAnalyticsStore().flush();
    }

    componentDidMount() {
        //  Load session data, set the app key, etc.
        this.context.appStore.mount(this.props.name);
        // Flush analytics buffer when navigating away
        window.addEventListener('beforeunload', this.handleBeforeUnload);
    }

    componentWillUnmount() {
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
    }

    render() {
        let component;

        let pendingSpinner =
            <div className="text-center my-5">
                <Spinner animation="border" role="status" className="fs-3" style={{width: "3rem", height: "3rem"}}>
                    <span className="sr-only">Loading...</span>
                </Spinner>
            </div>;

        switch (this.context.appStore.state) {
            case States.initial:
                component = pendingSpinner;
                break;

            case States.pending:
                component = pendingSpinner;
                break;

            case States.done:
                let navigation = null, cookiesModal = null;
                let showNavigation = this.props.showAppNavigation;

                /* Populate the app navigation component if show app nav is true */
                if ( showNavigation ) {
                    navigation =
                        <div className="nav-wrapper">
                            {this.props.navigation}
                        </div>;
                }

                /* If Cookies are disabled, show a modal message informing user to enable cookies */
                if (this.context.appStore.hasCookiesDisabled()) {
                    cookiesModal = <Cookies/>;
                }

                let skipNavigation;
                if (this.props.showSkipNavigation) {
                    skipNavigation = <AccessibleHashlink id="skip-nav" className="position-absolute sr-shortcut" to="#main-content" replace>Skip to main content</AccessibleHashlink>;
                }

                let bannerModal;
                if ( !isEmpty(this.props.bannerModal) ){
                    bannerModal = <BannerModal {...this.props.bannerModal} />
                }

                let content =
                    <>
                        <FeedbackMessages/>
                        <main role="main" className={classnames("content", {"position-relative p-3 pt-5": showNavigation}, {"h-100": this.props.isTopbar})} id="main-content">
                            {this.props.routes}
                        </main>
                    </>;

                if (showNavigation) {
                    content =
                        <>
                            {navigation}
                            <div className={classnames({"flex-grow-1": showNavigation})}>
                                <FeedbackMessages/>
                                <main role="main" className={classnames("content", {"position-relative p-3 pt-5": showNavigation}, {"h-100": this.props.isTopbar})} id="main-content">
                                    {this.props.routes}
                                </main>
                            </div>
                        </>
                }

                if (this.props.isTopbar) {
                    component =
                        <>
                            <Announcer/>
                            <div className="bg-brand d-flex align-items-end flex-column axdd-topbar axdd-font-open-sans vh-100">
                                {skipNavigation}
                                {this.props.header}
                                <div className="bg-body w-100 flex-fill text-body">
                                    <div className="container-xl h-100 py-3">
                                        {content}
                                    </div>
                                </div>
                                {this.props.footer}
                            </div>
                            {cookiesModal}
                            {bannerModal}
                            <PageTracking/>
                        </>

                } else {
                    component =
                        <>
                            <Announcer/>
                            {skipNavigation}
                            <div className="app-wrapper">
                                {this.props.header}
                                <div className={classnames('app-content', {"d-flex offset-50": showNavigation})}>
                                    <div className="container-xxl p-0">
                                        {content}
                                    </div>
                                </div>
                                {this.props.footer}
                            </div>
                            {cookiesModal}
                            {bannerModal}
                            <PageTracking />
                        </>;
                }

                break;

            case States.maintenance:
                component = this.props.maintenancePage ?? <Maintenance title={this.props.title} />
                break;

            case States.unauthenticated:
                component = <Login title={this.props.title} />;
                break;

            case States.unauthorized:
                component = <Unauthorized />;
                break;

            case States.error:
                component = <Error />;
                break;

            default:
                component = <UnknownState name="App"/>;
                break;
        }

        return (
            <DocumentTitle title={this.props.title}>
                {component}
            </DocumentTitle>
        );
    }
}

App.propTypes = {
    name: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    routes: PropTypes.object.isRequired,
    showAppNavigation: PropTypes.bool,
    showSkipNavigation: PropTypes.bool,
    bannerModal: PropTypes.object,
    isTopbar: PropTypes.bool,
};

App.defaultProps = {
    showAppNavigation: true,
    showSkipNavigation: true,
    header: <AppHeaderDefault />,
    footer: <AppFooterDefault />,
    maintenancePage: null,
    navigation: <></>,
    bannerModal: {},
    isTopbar: false
};

export default observer(App);
